import React, { useState, useEffect, useRef } from 'react';
import { 
  Heart, ArrowLeft, CheckCircle2, AlertCircle, RotateCcw, Home, 
  CheckSquare, Square, BookOpen, Layers, Trash2, Play, MousePointer2, Keyboard 
} from 'lucide-react'; 
import { collection, getDocs, doc, setDoc, increment, updateDoc } from 'firebase/firestore';
import { fireDB as db, appId } from '../services/firebase';
import { playSound, getChoseong } from '../utils/helpers';
import { LAW_DATA, LAW_CONFIG } from '../data/laws'; 

export default function Odaptong({ user, setView, onHome }) {
  const [allWrongData, setAllWrongData] = useState([]);
  const [viewState, setViewState] = useState('loading'); 
  const [selectedLaw, setSelectedLaw] = useState(null);
  
  const [playQuestions, setPlayQuestions] = useState([]); 
  const [gameMode, setGameMode] = useState('expert'); 
  const [qIndex, setQIndex] = useState(0);
  const [lives, setLives] = useState(3);
  const [feedback, setFeedback] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [expertStep, setExpertStep] = useState(0);
  const [currentChoices, setCurrentChoices] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => {
    const fetchWrongAnswers = async () => {
      try {
        const wrongRef = collection(db, 'artifacts', appId, 'users', user.uid, 'wrong_answers');
        const snap = await getDocs(wrongRef);
        const list = [];
        
        let allQuestions = [];

        // 1. 행정법 공통 데이터
        LAW_DATA.forEach(item => {
            allQuestions.push({ ...item, law: item.law || "행정법" });
        });

        // 2. 개별법 데이터 (LAW_CONFIG)
        Object.entries(LAW_CONFIG).forEach(([lawName, config]) => {
            if (config.data) {
                config.data.forEach(item => {
                    // ▼▼▼ [핵심 수정] 원본의 '시행령/시행규칙' 정보를 조문명(article)에 보존 ▼▼▼
                    let finalArticle = item.article || "관련 조문";
                    
                    // item.law가 '시행령', '시행규칙' 등을 포함하면 조문명 앞에 붙임
                    if (item.law && (item.law.includes('시행령') || item.law.includes('시행규칙'))) {
                        // 이미 조문명에 안 들어있을 때만 추가
                        if (!finalArticle.includes(item.law)) {
                            finalArticle = `${item.law} ${finalArticle}`;
                        }
                    }

                    // law 속성은 카테고리 분류를 위해 메인 법률명(예: 부동산공시법)으로 통일
                    allQuestions.push({ 
                        ...item, 
                        law: lawName,
                        article: finalArticle 
                    });
                });
            }
        });
        
        snap.forEach(docSnap => {
          const dbData = docSnap.data();
          if ((dbData.count >= 3 || dbData.isBookmarked) && dbData.id) {
            const originalData = allQuestions.find(item => String(item.id) === String(dbData.id));
            if (originalData) {
               list.push({ ...dbData, ...originalData, law: originalData.law, article: originalData.article });
            }
          }
        });

        const uniqueList = list.filter((v, i, a) => a.findIndex(t => t.id === v.id) === i);
        setAllWrongData(uniqueList);
        setViewState('categories');
      } catch (e) {
        console.error(e);
        alert("오답 데이터 로딩 실패");
        onHome();
      }
    };
    fetchWrongAnswers();
  }, [user, onHome]);

  const currentQ = playQuestions[qIndex];
  const targetKeywordObj = currentQ?.keywords?.[0];

  useEffect(() => {
    if (viewState === 'playing' && targetKeywordObj) {
      const newChoices = [targetKeywordObj.word, targetKeywordObj.distractor].sort(() => Math.random() - 0.5);
      setCurrentChoices(newChoices);
    }
  }, [targetKeywordObj, viewState]);

  const handleExclude = async (item) => {
    if (!window.confirm(`[${item.article}]을(를) 오답통에서 제외하시겠습니까?`)) return;
    try {
        await setDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'wrong_answers', item.id.toString()), { 
            isBookmarked: false,
            count: 0
        }, { merge: true });
        setAllWrongData(prev => prev.filter(q => q.id !== item.id));
    } catch (e) {
        console.error(e);
        alert("오류 발생");
    }
  };

  const prepareGame = (questionsList) => {
      if (questionsList.length === 0) {
          alert("학습할 문제가 없습니다.");
          return;
      }
      const shuffled = [...questionsList].sort(() => Math.random() - 0.5);
      setPlayQuestions(shuffled);
      setViewState('mode-select'); 
  };

  const launchGame = (mode) => {
      setGameMode(mode);
      setQIndex(0);
      setLives(3);
      setFeedback(null);
      setInputValue('');
      setExpertStep(0);
      setViewState('playing');
  };

  const handleGameCorrect = async () => {
    playSound('correct');
    setFeedback('correct');
    try {
        await updateDoc(doc(db, 'artifacts', appId, 'users', user.uid, 'profile', 'info'), {
            totalSolved: increment(1),
            correctCount: increment(1)
        });
    } catch(e){}

    setTimeout(() => {
        setFeedback(null);
        setInputValue('');
        setExpertStep(0);
        if (qIndex + 1 < playQuestions.length) {
            setQIndex(prev => prev + 1);
        } else {
            setViewState('clear');
        }
    }, 1500);
  };

  const handleGameWrong = async () => {
    playSound('wrong');
    setFeedback('wrong');
    
    setTimeout(() => {
        setFeedback(null);
        setInputValue('');
        if (gameMode === 'expert') {
            if (expertStep < 2) setExpertStep(prev => prev + 1);
            else {
                setExpertStep(0);
                if (qIndex + 1 < playQuestions.length) setQIndex(prev => prev + 1);
                else setViewState('clear');
            }
        } else {
            if (qIndex + 1 < playQuestions.length) setQIndex(prev => prev + 1);
            else setViewState('clear');
        }
    }, 1500);
  };

  const submitGameInput = (e) => {
    if (e) e.preventDefault();
    if (inputValue.trim() === targetKeywordObj.word) handleGameCorrect();
    else handleGameWrong();
  };

  if (viewState === 'loading') return <div className="text-center py-20 text-slate-400">로딩 중...</div>;

  // [화면 A] 카테고리 선택
  if (viewState === 'categories') {
      const counts = allWrongData.reduce((acc, item) => {
          const lawName = item.law || "기타";
          acc[lawName] = (acc[lawName] || 0) + 1;
          return acc;
      }, {});
      const totalCount = allWrongData.length;

      // ▼▼▼ [수정] 법률 카테고리 분류 로직 개선 ▼▼▼
      const adminLaws = [];
      const indivLaws = [];

      // 행정법 카테고리에 포함될 법률들을 명시 (나머지는 개별법으로 분류됨)
      const ADMIN_GROUP = [
          "행정법", // 기본 개념
          "행정기본법",
          "행정절차법",
          "행정심판법",
          "행정소송법",
          "헌법 및 헌법재판소법"
      ];

      Object.keys(counts).sort().forEach(law => {
          if (ADMIN_GROUP.includes(law)) {
              adminLaws.push(law);
          } else {
              indivLaws.push(law);
          }
      });
      // ▲▲▲ [수정 완료] ▲▲▲

      // 버튼 렌더링 헬퍼 함수
      const renderLawButtons = (laws) => (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
              {laws.map(law => (
                  <button 
                      key={law}
                      onClick={() => { setSelectedLaw(law); setViewState('list'); }}
                      className="aspect-square bg-slate-800 border border-slate-700 hover:border-blue-500/50 hover:bg-slate-750 rounded-lg flex flex-col items-center justify-center p-2 transition shadow-sm group"
                  >
                      <BookOpen className="w-6 h-6 text-slate-600 group-hover:text-blue-400 mb-2 transition" />
                      <h3 className="font-bold text-xs text-slate-200 text-center break-keep leading-snug px-1">{law}</h3>
                      <span className="mt-1.5 bg-slate-900 text-slate-400 text-[10px] px-1.5 py-0.5 rounded font-medium">
                          {counts[law]}개
                      </span>
                  </button>
              ))}
          </div>
      );

      return (
          <div key="categories" className="space-y-6 animate-fadeIn pb-10">
              <div className="flex items-center justify-between mb-2">
                  <h2 className="text-xl font-bold text-white flex items-center">
                      <CheckSquare className="mr-2 text-orange-400" /> 오답통
                  </h2>
                  <button onClick={onHome} className="bg-slate-700 px-3 py-1.5 rounded text-white text-xs hover:bg-slate-600 transition">메인으로</button>
              </div>

              <button 
                  onClick={() => prepareGame(allWrongData)}
                  disabled={totalCount === 0}
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white p-4 rounded-xl shadow-lg flex items-center justify-between group transition transform hover:scale-[1.01]"
              >
                  <div className="flex items-center">
                      <div className="bg-white/20 p-2 rounded-full mr-3 group-hover:rotate-12 transition">
                          <RotateCcw className="w-5 h-5 text-white" />
                      </div>
                      <div className="text-left">
                          <h3 className="text-base font-bold text-white">오답통 집중 연습 (전체)</h3>
                          <p className="text-blue-100 text-xs">모든 오답 조문을 한 번에 테스트합니다.</p>
                      </div>
                  </div>
                  <span className="text-lg font-bold bg-white/20 px-3 py-1 rounded text-white">{totalCount}개</span>
              </button>

              {/* 1. 행정법 섹션 */}
              {adminLaws.length > 0 && (
                  <div className="animate-fadeIn">
                      <h3 className="text-slate-400 text-sm font-bold mb-3 border-l-4 border-indigo-500 pl-3">행정법</h3>
                      {renderLawButtons(adminLaws)}
                  </div>
              )}

              {/* 2. 개별법 섹션 */}
              {indivLaws.length > 0 && (
                  <div className="animate-fadeIn">
                      <h3 className="text-slate-400 text-sm font-bold mb-3 mt-2 border-l-4 border-emerald-500 pl-3">개별법</h3>
                      {renderLawButtons(indivLaws)}
                  </div>
              )}
              
              {totalCount === 0 && <div className="text-center py-20 text-slate-500 text-sm">오답 데이터가 없습니다. 👍</div>}
          </div>
      );
  }

  // [화면 B] 리스트
  if (viewState === 'list') {
      const list = allWrongData.filter(item => item.law === selectedLaw);
      return (
          <div key="list" className="space-y-4 animate-fadeIn h-[calc(100vh-150px)] flex flex-col">
              <div className="flex items-center justify-between pb-4 border-b border-slate-700 shrink-0">
                  <h2 className="text-lg font-bold text-white flex items-center">
                      <Layers className="mr-2 text-indigo-400 w-5 h-5" /> {selectedLaw} <span className="ml-2 text-xs text-slate-400">({list.length}개)</span>
                  </h2>
                  <button onClick={() => setViewState('categories')} className="text-slate-400 hover:text-white flex items-center text-xs border border-slate-700 px-2 py-1 rounded bg-slate-800">
                      <ArrowLeft className="w-3 h-3 mr-1" /> 뒤로가기
                  </button>
              </div>
              <div className="pb-2 shrink-0">
                  <button onClick={() => prepareGame(list)} className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-3 rounded-lg shadow-lg flex items-center justify-center transition text-sm">
                      <Play className="w-4 h-4 mr-2 fill-white" /> 연습 시작
                  </button>
              </div>
              <div className="flex-1 overflow-y-auto space-y-2 pr-1 custom-scrollbar">
                  {list.map(item => (
                      <div key={item.id} className="bg-slate-800 p-3 rounded-lg border border-slate-700 flex justify-between items-center group hover:border-slate-500 transition">
                          <div className="flex-1 min-w-0 mr-3">
                              <span className="text-indigo-400 text-[11px] font-bold block mb-0.5">{item.article}</span>
                              <p className="text-slate-300 text-xs line-clamp-1">{item.text}</p>
                          </div>
                          <button onClick={() => handleExclude(item)} className="p-1.5 text-slate-600 hover:text-red-400 hover:bg-red-900/20 rounded transition flex flex-col items-center shrink-0">
                              <Trash2 className="w-4 h-4" />
                          </button>
                      </div>
                  ))}
              </div>
          </div>
      );
  }

  // [화면: 모드 선택]
  if (viewState === 'mode-select') {
      return (
          <div key="mode-select" className="space-y-6 animate-fadeIn py-10">
              <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-white mb-2">학습 모드 선택</h2>
                  <p className="text-slate-400 text-sm">어떤 방식으로 복습하시겠습니까?</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button onClick={() => launchGame('beginner')} className="bg-slate-800 hover:bg-indigo-900/40 border border-slate-700 hover:border-indigo-500 p-6 rounded-xl flex flex-col items-center justify-center group transition shadow-lg h-48">
                      <div className="bg-slate-700 p-4 rounded-full mb-4 group-hover:bg-indigo-600 transition"><MousePointer2 className="w-8 h-8 text-white" /></div>
                      <h3 className="text-lg font-bold text-white mb-1">초보자 모드</h3>
                      <p className="text-slate-400 text-xs text-center">객관식 선택형으로<br/>가볍게 복습합니다.</p>
                  </button>
                  <button onClick={() => launchGame('expert')} className="bg-slate-800 hover:bg-orange-900/40 border border-slate-700 hover:border-orange-500 p-6 rounded-xl flex flex-col items-center justify-center group transition shadow-lg h-48">
                      <div className="bg-slate-700 p-4 rounded-full mb-4 group-hover:bg-orange-600 transition"><Keyboard className="w-8 h-8 text-white" /></div>
                      <h3 className="text-lg font-bold text-white mb-1">숙련자 모드</h3>
                      <p className="text-slate-400 text-xs text-center">직접 빈칸을 입력하며<br/>완벽하게 암기합니다.</p>
                  </button>
              </div>
              <div className="text-center mt-8">
                  <button onClick={() => setViewState('categories')} className="text-slate-500 hover:text-white text-sm underline">취소하고 돌아가기</button>
              </div>
          </div>
      );
  }

  // [화면 C] 결과
  if (viewState === 'clear') return (
      <div key="clear" className="text-center py-12 bg-slate-800 rounded-xl shadow-lg border border-slate-700 flex flex-col items-center">
        <CheckCircle2 className="w-20 h-20 text-green-400 mb-6" />
        <h2 className="text-3xl font-bold text-white mb-2">학습 완료!</h2>
        <div className="mt-6 space-y-3 w-full max-w-xs">
            <button onClick={() => setViewState('categories')} className="w-full bg-indigo-600 text-white px-6 py-3 rounded-lg font-bold">오답통 목록으로</button>
            <button onClick={onHome} className="w-full bg-slate-700 text-white px-6 py-3 rounded-lg font-bold">메인으로</button>
        </div>
      </div>
  );

  if (viewState === 'game-over') return (
      <div key="gameover" className="text-center py-12 bg-slate-800 rounded-xl shadow-lg border border-slate-700 flex flex-col items-center">
        <AlertCircle className="w-20 h-20 text-red-500 mb-6" />
        <h2 className="text-3xl font-bold text-white mb-2">도전 실패</h2>
        <div className="mt-6 space-y-3 w-full max-w-xs">
            <button onClick={() => prepareGame(playQuestions)} className="w-full bg-slate-700 text-white px-6 py-3 rounded-lg font-bold"><RotateCcw className="w-5 h-5 inline mr-2"/>다시 도전</button>
            <button onClick={() => setViewState('categories')} className="w-full bg-slate-800 text-slate-400 px-6 py-3 rounded-lg font-bold border border-slate-700">목록으로</button>
        </div>
      </div>
  );

  // [화면 D] 게임 플레이
  if (viewState === 'playing' && currentQ && targetKeywordObj) {
      const fullText = currentQ.text || "";
      const targetWord = targetKeywordObj.word.trim();
      const parts = fullText.split(targetWord);
      let textBefore = fullText;
      let textAfter = "";
      if (parts.length > 1) {
          textBefore = parts[0];
          textAfter = parts.slice(1).join(targetWord);
      }
      // ▼▼▼ [수정] 조문명 앞에 법률 이름 표시 (예: "부동산공시법 시행령 제18조") ▼▼▼
      const lawName = currentQ.law || "";
      const rawArticle = currentQ.article || "관련 조문";
      
      let articleTitle = rawArticle;
      
      // 1. 법률 이름이 존재하고
      // 2. 조문 텍스트가 법률 이름으로 시작하지 않으면 (예: "시행령..." 으로 시작하면)
      // -> 앞에 법률 이름을 붙여줌
      if (lawName && !rawArticle.startsWith(lawName)) {
          articleTitle = `${lawName} ${rawArticle}`;
      }

      return (
        <div key={currentQ.id} className="max-w-2xl mx-auto relative p-1">
          <div className="absolute inset-0 border-[6px] border-double border-amber-600/50 rounded-xl pointer-events-none"></div>
          <div className="relative z-10 p-2">
            <div className="flex justify-between items-center mb-6 bg-slate-800 p-4 rounded-xl shadow-md border border-slate-700">
                <div></div> {/* 하트 삭제됨 */}
                <div className="flex items-center space-x-4">
                    <span className="text-orange-400 font-bold text-xs bg-orange-900/30 px-2 py-1 rounded">
                        {gameMode === 'beginner' ? '초보자 모드' : '숙련자 모드'}
                    </span>
                    <span className="text-slate-400 font-medium text-sm ml-2">Q-{qIndex + 1}/{playQuestions.length}</span>
                </div>
            </div>
            <div className="bg-slate-800 p-8 rounded-xl shadow-lg min-h-[300px] flex flex-col justify-between border border-slate-700 relative">
                <div className="flex justify-between items-center w-full mb-6 pb-3 border-b border-slate-700/50">
                    <div className="bg-indigo-600 text-white text-xs font-bold px-3 py-1.5 rounded flex items-center shadow-md">
                        <BookOpen className="w-3 h-3 mr-1.5" />
                        {articleTitle}
                    </div>
                    <button onClick={() => setViewState('categories')} className="flex items-center text-xs text-slate-400 hover:text-white border border-slate-600 hover:bg-slate-700 rounded px-3 py-1.5 transition">
                        <ArrowLeft className="w-3 h-3 mr-1" /> 그만하기
                    </button>
                </div>
                {/* ▼▼▼ [수정] Game.js와 100% 동일한 스타일(폰트 크기, 굵기, 색상)로 복구 ▼▼▼ */}
                <div className="flex-1 w-full flex flex-col items-center justify-center py-8 px-4 bg-[#111827] border border-slate-700 rounded-3xl shadow-2xl mb-6"> 
                    <div className="w-fit max-w-full text-left text-base md:text-lg leading-relaxed font-medium text-slate-200 break-all">
                        <span>{textBefore}</span>
                        <span className={`mx-1 px-3 border-b-2 transition-colors duration-300 inline-block font-bold min-w-[60px] text-center align-bottom ${
                            feedback === 'correct' 
                                ? 'border-green-500 text-green-400' 
                                : feedback === 'wrong' 
                                    ? 'border-red-500 text-red-500' 
                                    : 'border-indigo-500 text-indigo-400 bg-slate-900/50'
                        }`}>
                            {feedback === 'correct' 
                                ? targetWord 
                                : (feedback === 'wrong' && (gameMode === 'beginner' || expertStep >= 2) 
                                    ? targetWord 
                                    : "???")
                            }
                        </span>
                        <span>{textAfter}</span>
                    </div>
                </div>
                <div className="mt-8">
                    {feedback === 'correct' && <div className="bg-green-900/30 border border-green-500/30 text-green-400 p-4 rounded-lg flex items-center justify-center animate-bounce"><CheckCircle2 className="w-6 h-6 mr-2" /> 정답입니다!</div>}
                    {feedback === 'wrong' && <div className="bg-red-900/30 border border-red-500/30 text-red-400 p-4 rounded-lg flex items-center justify-center animate-bounce"><AlertCircle className="w-6 h-6 mr-2" /> 오답입니다. {(gameMode === 'beginner' || expertStep >= 2) && <span className="text-sm ml-2 text-slate-300">정답: {targetKeywordObj.word}</span>}</div>}
                    {!feedback && (
                        <div className="animate-fadeIn">
                            {gameMode === 'expert' && (
                                <div className="space-y-4">
                                    {expertStep === 0 && (
                                        <form onSubmit={submitGameInput} className="flex">
                                        <input ref={inputRef} type="text" className="flex-1 bg-slate-900 border border-slate-600 rounded-l-lg p-4 text-white placeholder-slate-500 focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="직접 입력하세요" value={inputValue} onChange={(e)=>setInputValue(e.target.value)} autoFocus />
                                        <button type="submit" className="bg-indigo-600 text-white px-6 rounded-r-lg font-bold">확인</button>
                                        </form>
                                    )}
                                    {expertStep === 1 && (
                                        <div className="space-y-4">
                                        <div className="bg-indigo-900/30 border border-indigo-500/30 p-4 rounded-xl text-center"><p className="text-slate-400 text-xs mb-2 font-bold">💡 초성 힌트</p><span className="text-2xl tracking-[0.5em] font-mono text-indigo-300 ml-4">{getChoseong(targetKeywordObj.word)}</span></div>
                                        <form onSubmit={submitGameInput} className="flex">
                                            <input ref={inputRef} type="text" className="flex-1 bg-slate-900 border border-indigo-500/50 rounded-l-lg p-4 text-white outline-none" placeholder="힌트를 보고 다시 입력" value={inputValue} onChange={(e)=>setInputValue(e.target.value)} autoFocus />
                                            <button type="submit" className="bg-indigo-600 text-white px-6 rounded-r-lg font-bold">확인</button>
                                        </form>
                                        </div>
                                    )}
                                    {expertStep === 2 && (
                                        <div className="grid grid-cols-2 gap-4">
                                            {currentChoices.map((choice, i) => (
                                                <button key={i} onClick={() => { if(choice === targetKeywordObj.word) handleGameCorrect(); else handleGameWrong(); }} className="bg-slate-700 hover:bg-indigo-600 text-white p-4 rounded-xl transition font-bold text-lg">{choice}</button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                            {gameMode === 'beginner' && (
                                <div className="grid grid-cols-2 gap-4">
                                    {currentChoices.map((choice, i) => (
                                        <button key={i} onClick={() => { if(choice === targetKeywordObj.word) handleGameCorrect(); else handleGameWrong(); }} className="bg-slate-700 hover:bg-indigo-600 text-white p-4 rounded-xl transition font-bold text-lg">{choice}</button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
          </div>
        </div>
      );
  }
  return <div>Loading...</div>;
}